import { useCallback, useEffect, useRef, useState } from 'react';

export type WorkflowPanelSectionId =
  'defaults' | 'advanced' | 'guardrails' | 'assistant' | 'json';

export type WorkflowPanelSectionState = Record<WorkflowPanelSectionId, boolean>;

const STORAGE_KEY = 'workflow-editor-panel-sections';

const DEFAULT_SECTION_STATE: WorkflowPanelSectionState = {
  defaults: false,
  advanced: false,
  guardrails: false,
  assistant: false,
  json: false,
};

function readStoredSections(): WorkflowPanelSectionState {
  if (typeof window === 'undefined') {
    return DEFAULT_SECTION_STATE;
  }
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_SECTION_STATE;
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) {
      return DEFAULT_SECTION_STATE;
    }
    const record = parsed as Record<string, unknown>;
    return {
      defaults:
        typeof record.defaults === 'boolean'
          ? record.defaults
          : DEFAULT_SECTION_STATE.defaults,
      advanced:
        typeof record.advanced === 'boolean'
          ? record.advanced
          : DEFAULT_SECTION_STATE.advanced,
      guardrails:
        typeof record.guardrails === 'boolean'
          ? record.guardrails
          : DEFAULT_SECTION_STATE.guardrails,
      assistant:
        typeof record.assistant === 'boolean'
          ? record.assistant
          : DEFAULT_SECTION_STATE.assistant,
      json:
        typeof record.json === 'boolean'
          ? record.json
          : DEFAULT_SECTION_STATE.json,
    };
  } catch {
    return DEFAULT_SECTION_STATE;
  }
}

function writeStoredSections(state: WorkflowPanelSectionState): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // ignore quota / private mode errors
  }
}

/**
 * Persists bottom-dock collapsible open state for the workflow step panel.
 * On first mount, expands "defaults" when the step already has static input values.
 */
export function useWorkflowPanelSections(hasStaticInputDefaults: boolean) {
  const hydratedRef = useRef(false);
  const [sections, setSections] = useState<WorkflowPanelSectionState>(
    DEFAULT_SECTION_STATE,
  );

  useEffect(() => {
    if (hydratedRef.current) return;
    hydratedRef.current = true;
    const stored = readStoredSections();
    setSections({
      ...stored,
      defaults: stored.defaults || hasStaticInputDefaults,
    });
  }, [hasStaticInputDefaults]);

  const setSectionOpen = useCallback(
    (id: WorkflowPanelSectionId, open: boolean) => {
      setSections((prev) => {
        const next = { ...prev, [id]: open };
        writeStoredSections(next);
        return next;
      });
    },
    [],
  );

  return { sections, setSectionOpen };
}
